import { observer } from "mobx-react";
import React, { useContext, useEffect, useState } from "react";
import {
  Paper,
  Stack,
  Text,
  Title,
  Badge,
  Group,
  Divider,
  Box,
  CopyButton,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconCopy,
  IconCheck,
  IconExternalLink,
  IconAlertTriangle,
  IconX,
  IconInfoCircle,
  IconClock,
} from "@tabler/icons-react";
import { DecodeInvoiceStoreContext } from "../stores/DecodeInvoiceStore";
import {
  formatSatoshis,
  formatDateTime,
  formatRelativeTime,
} from "../helpers/formatters";
import { getAmbossLink } from "../helpers/types";
import RouteHintsDisplay from "./RouteHintsDisplay";
import {
  expiryState,
  formatDuration,
  invoiceChecks,
  CheckLevel,
  ExpiryState,
} from "../helpers/invoiceChecks";

/** Re-render every second so the expiry countdown ticks. */
const useNow = (): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
};

// Dark-page tints (AGENTS.md: no white blocks)
const TONES: Record<CheckLevel, { bg: string; border: string; color: string }> = {
  ok: { bg: "rgba(18, 184, 134, 0.10)", border: "rgba(18, 184, 134, 0.45)", color: "#63e6be" },
  warn: { bg: "rgba(250, 176, 5, 0.10)", border: "rgba(250, 176, 5, 0.5)", color: "#ffd43b" },
  fail: { bg: "rgba(250, 82, 82, 0.12)", border: "rgba(250, 82, 82, 0.55)", color: "#ff8787" },
  info: { bg: "rgba(77, 171, 247, 0.08)", border: "rgba(77, 171, 247, 0.35)", color: "#74c0fc" },
};

const CheckIcon: React.FC<{ level: CheckLevel; size?: number }> = ({ level, size = 16 }) => {
  const color = TONES[level].color;
  if (level === "ok") return <IconCheck size={size} color={color} />;
  if (level === "warn") return <IconAlertTriangle size={size} color={color} />;
  if (level === "fail") return <IconX size={size} color={color} />;
  return <IconInfoCircle size={size} color={color} />;
};

/**
 * Security: Sanitize alias to prevent XSS from malicious node operators
 * Removes HTML tags and limits length
 */
const sanitizeAlias = (alias: string | null | undefined): string | null => {
  if (!alias) return null;
  // Remove any HTML/script tags and limit to 50 chars
  return alias.replace(/<[^>]*>/g, "").substring(0, 50);
};

const DecodedInvoiceDisplay: React.FC = observer(() => {
  const store = useContext(DecodeInvoiceStoreContext);

  const now = useNow();

  if (!store.decodedInvoice) {
    return null;
  }

  const invoice = store.decodedInvoice.data;
  const expiry = expiryState(invoice, now);
  const checks = invoiceChecks(invoice, now);

  return (
    <Paper
      p="lg"
      radius="md"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.4)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
      }}
    >
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3} c="white">
            Decoded Invoice
          </Title>
          <Group gap="xs">
            {store.network && (
              <Badge variant="light" color="blue">
                {store.network}
              </Badge>
            )}
            {expiry.kind === "expired" ? (
              <Badge color="red">Expired</Badge>
            ) : expiry.kind === "valid" && expiry.soon ? (
              <Badge color="yellow">Expires soon</Badge>
            ) : (
              <Badge color="green">Valid</Badge>
            )}
          </Group>
        </Group>

        <ExpiryBanner expiry={expiry} expiryDate={store.expiryDate} />

        <Stack gap={6}>
          {checks.map((check) => (
            <Box
              key={check.title}
              px="sm"
              py={8}
              style={{
                backgroundColor: TONES[check.level].bg,
                borderLeft: `3px solid ${TONES[check.level].border}`,
                borderRadius: 4,
              }}
            >
              <Group gap={8} wrap="nowrap" align="flex-start">
                <Box mt={2} style={{ flexShrink: 0 }}>
                  <CheckIcon level={check.level} />
                </Box>
                <Box>
                  <Text size="sm" c="white" fw={500}>
                    {check.title}
                  </Text>
                  {check.detail && (
                    <Text size="xs" c="dimmed" mt={2}>
                      {check.detail}
                    </Text>
                  )}
                </Box>
              </Group>
            </Box>
          ))}
          <Text size="xs" c="dimmed">
            These checks read the invoice and Rizful's copy of the public Lightning
            graph. They don't contact the payee or send anything, so they can't promise
            a payment will succeed.
          </Text>
        </Stack>

        <Divider color="rgba(255, 255, 255, 0.1)" />

        {/* Amount */}
        {store.amountSatoshis !== null && (
          <InfoRow
            label="Amount"
            value={formatSatoshis(store.amountSatoshis)}
            highlight
          />
        )}

        {store.amountSatoshis === null && (
          <InfoRow
            label="Amount"
            value="Any amount (no amount specified)"
            muted
          />
        )}

        {/* Destination (payee node) */}
        {store.destination && (
          <PayeeNodeCard
            pubkey={store.destination}
            alias={store.destinationAlias}
            inGraph={invoice.destinationInGraph}
            keysend={invoice.destinationKeysend}
            routeHintCount={store.routeHints.length}
          />
        )}

        {/* Description */}
        {store.description && (
          <InfoRow label="Description" value={store.description} />
        )}

        {/* Timestamps */}
        {store.createdDate && (
          <InfoRow
            label="Created"
            value={`${formatDateTime(store.createdDate)} (${formatRelativeTime(
              store.createdDate
            )})`}
          />
        )}

        {store.expiryDate && (
          <InfoRow
            label={`Expires (${formatDuration(invoice.expiry ?? 0)} after creation)`}
            value={`${formatDateTime(store.expiryDate)} (${formatRelativeTime(
              store.expiryDate
            )})`}
            highlight={expiry.kind === "expired"}
            highlightColor="red"
          />
        )}

        <Divider color="rgba(255, 255, 255, 0.1)" />

        {/* Payment Hash */}
        {store.paymentHash && (
          <HashRow label="Payment Hash" value={store.paymentHash} />
        )}

        {/* Payment Secret */}
        {store.paymentSecret && (
          <HashRow label="Payment Secret" value={store.paymentSecret} />
        )}

        {/* Min Final CLTV Expiry */}
        {store.minFinalCltvExpiry !== null && (
          <InfoRow
            label="Min Final CLTV Expiry"
            value={`${store.minFinalCltvExpiry} blocks`}
          />
        )}

        {/* Route Hints */}
        {store.routeHints.length > 0 && (
          <>
            <Divider color="rgba(255, 255, 255, 0.1)" />
            <RouteHintsDisplay
              routeHints={store.routeHints}
              pubkeyAliases={store.pubkeyAliases}
            />
          </>
        )}
      </Stack>
    </Paper>
  );
});

/** The big, live expiry line: green while valid, amber near the end, red once expired. */
const ExpiryBanner: React.FC<{ expiry: ExpiryState; expiryDate: Date | null }> = ({
  expiry,
  expiryDate,
}) => {
  if (expiry.kind === "unknown") return null;

  const tone =
    expiry.kind === "expired" ? TONES.fail : expiry.soon ? TONES.warn : TONES.ok;
  const headline =
    expiry.kind === "expired"
      ? `Expired ${formatDuration(expiry.secondsAgo)} ago`
      : `Valid for ${formatDuration(expiry.secondsLeft)}`;
  const sub =
    expiry.kind === "expired"
      ? "This invoice can't be paid any more. Ask the recipient for a new one."
      : expiry.soon
      ? "Almost out of time: pay it now, or ask for a new invoice."
      : expiryDate
      ? `Can be paid until ${formatDateTime(expiryDate)}.`
      : "";

  return (
    <Box
      p="md"
      role="status"
      style={{
        backgroundColor: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: 8,
      }}
    >
      <Group gap="sm" wrap="nowrap" align="center">
        <IconClock size={28} color={tone.color} style={{ flexShrink: 0 }} />
        <Box>
          <Text
            fw={700}
            size="lg"
            style={{ color: tone.color, fontVariantNumeric: "tabular-nums" }}
          >
            {headline}
          </Text>
          {sub && (
            <Text size="sm" c="gray.3">
              {sub}
            </Text>
          )}
        </Box>
      </Group>
    </Box>
  );
};

interface PayeeNodeCardProps {
  pubkey: string;
  alias: string | null;
  inGraph: boolean | null;
  keysend: boolean | null;
  routeHintCount: number;
}

/** The destination node, as our copy of the public Lightning graph knows it. */
const PayeeNodeCard: React.FC<PayeeNodeCardProps> = ({
  pubkey,
  alias,
  inGraph,
  keysend,
  routeHintCount,
}) => {
  const safeAlias = sanitizeAlias(alias);
  return (
    <Box
      p="sm"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.25)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        borderRadius: 6,
      }}
    >
      <Text size="xs" c="dimmed" mb={4}>
        Payee node
      </Text>
      <Text size="md" c="white" fw={600} mb={6} style={{ wordBreak: "break-word" }}>
        {safeAlias || (inGraph ? "No alias set" : "Unknown node")}
      </Text>
      <Group gap={6} mb={8}>
        {inGraph === true && (
          <Badge size="sm" variant="light" color="teal">
            Public node
          </Badge>
        )}
        {inGraph === false && (
          <Badge size="sm" variant="light" color={routeHintCount > 0 ? "gray" : "yellow"}>
            Not in the public graph
          </Badge>
        )}
        {keysend === true && (
          <Badge size="sm" variant="light" color="grape">
            Keysend
          </Badge>
        )}
        {routeHintCount > 0 && (
          <Badge size="sm" variant="light" color="gray">
            {routeHintCount} route hint{routeHintCount > 1 ? "s" : ""}
          </Badge>
        )}
      </Group>
      <PubkeyRow label="Public key" value={pubkey} />
    </Box>
  );
};

interface InfoRowProps {
  label: string;
  value: string;
  highlight?: boolean;
  highlightColor?: string;
  muted?: boolean;
}

const InfoRow: React.FC<InfoRowProps> = ({
  label,
  value,
  highlight,
  highlightColor = "yellow",
  muted,
}) => (
  <Box>
    <Text size="xs" c="dimmed" mb={2}>
      {label}
    </Text>
    <Text
      size="sm"
      c={muted ? "dimmed" : highlight ? highlightColor : "white"}
      fw={highlight ? 600 : 400}
    >
      {value}
    </Text>
  </Box>
);

interface HashRowProps {
  label: string;
  value: string;
}

const HashRow: React.FC<HashRowProps> = ({ label, value }) => (
  <Box>
    <Text size="xs" c="dimmed" mb={2}>
      {label}
    </Text>
    <Group gap="xs" wrap="nowrap">
      <Text
        size="xs"
        c="white"
        style={{
          fontFamily: "monospace",
          wordBreak: "break-all",
          flex: 1,
        }}
      >
        {value}
      </Text>
      <CopyButton value={value}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? "Copied!" : "Copy"} withArrow>
            <ActionIcon
              variant="subtle"
              color={copied ? "green" : "gray"}
              onClick={copy}
              size="sm"
            >
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  </Box>
);

interface PubkeyRowProps {
  label: string;
  value: string;
  alias?: string | null;
}

const PubkeyRow: React.FC<PubkeyRowProps> = ({ label, value, alias }) => {
  const safeAlias = sanitizeAlias(alias);
  return (
    <Box>
      <Text size="xs" c="dimmed" mb={2}>
        {label}
      </Text>
      {safeAlias && (
        <Text size="sm" c="white" fw={600} mb={4}>
          {safeAlias}
        </Text>
      )}
      <Group gap="xs" wrap="nowrap">
        <Text
          size="xs"
          c="cyan"
          component="a"
          href={getAmbossLink(value)}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontFamily: "monospace",
            wordBreak: "break-all",
            flex: 1,
            textDecoration: "none",
          }}
        >
          {value}
          <IconExternalLink
            size={12}
            style={{ marginLeft: 4, verticalAlign: "middle" }}
          />
        </Text>
        <CopyButton value={value}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? "Copied!" : "Copy"} withArrow>
              <ActionIcon
                variant="subtle"
                color={copied ? "green" : "gray"}
                onClick={copy}
                size="sm"
              >
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
    </Box>
  );
};

export default DecodedInvoiceDisplay;
